/**
 * Façade over the shared `colorPalette` module that names the call-site
 * intent ("this chip is for an assignee"). The actual palette lives in
 * `./colorPalette` so that 領域 chips and assignee chips draw from the
 * same 17 slots and a `colorIndex` round-trips identically across
 * surfaces.
 *
 * Resolution order, in priority:
 *   1. `AssigneeDoc.colorIndex` — frozen at creation, survives renames.
 *   2. `assigneeColorIndex(name)` from `@mct/shared` — deterministic
 *      hash, used for legacy free-text WBS rows that haven't been
 *      backfilled.
 *   3. UNASSIGNED_CLASSES — for the null / blank case.
 *
 * The exported function names are unchanged from the pre-refactor API
 * so unrelated work-streams (e.g. `feature/backfill-assignee-normalization`)
 * keep importing them without churn.
 */
import { assigneeColorIndex, type AssigneeDoc } from '@mct/shared';
import {
  colorClassesByIndex,
  PALETTE,
  PALETTE_SIZE,
  UNASSIGNED_CLASSES,
} from './colorPalette';

export { PALETTE, PALETTE_SIZE, UNASSIGNED_CLASSES };

/**
 * Resolve palette classes for a free-text name. Used as a fallback when
 * an `AssigneeDoc` is unavailable.
 */
export function assigneeColorClasses(name: string | null | undefined): string {
  if (!name) return UNASSIGNED_CLASSES;
  return colorClassesByIndex(assigneeColorIndex(name));
}

/**
 * Resolve palette classes from a stored `colorIndex`. Out-of-range
 * indices wrap with modulus so the chip never blanks out.
 */
export function assigneeColorClassesByIndex(
  colorIndex: number | null | undefined,
): string {
  return colorClassesByIndex(colorIndex);
}

/**
 * Preferred entrypoint when you have a doc-or-name pair. Resolves the
 * frozen color from the doc when present (so renames don't change the
 * color), falling back to the deterministic name hash otherwise.
 */
export function resolveAssigneeColorClasses(
  assignee: Pick<AssigneeDoc, 'colorIndex'> | null | undefined,
  fallbackName: string | null | undefined,
): string {
  if (assignee && Number.isFinite(assignee.colorIndex)) {
    return colorClassesByIndex(assignee.colorIndex);
  }
  return assigneeColorClasses(fallbackName);
}
